import {
  RootState,
  TWalletAdapterState,
  TWalletSessionState,
} from '@/types/store'
import {ActionContext, Module} from 'vuex'
import {AccessorHandler} from '@simpli/vuex-typescript'
import {WalletPlatform} from '@/enums/WalletPlatform'
import {$} from '@/facade'
import {cloneDeep} from 'lodash'
import {
  LastWalletConnected,
  PopulateSdkStatusParams,
} from '@/model/wallets/types/WalletTypes'
import {
  RejectedByUser,
  UnimplementedWallet,
  WalletNotConnected,
} from '@/model/wallets/types/WalletErrors'
import {TWalletImplementations} from '@/model/wallets/types/WalletTypes'
import {TWalletNetwork} from '@/model/wallets/types/WalletNetwork'
import {siteNetwork, walletNetworksInUse} from '@/helpers/NetworkModeRegistry'
import {getWalletAdapter} from '@/store/model/wallets/adapters/WalletAdapterRegistry'
import {WalletAdapter} from '@/store/model/wallets/adapters/WalletAdapter'
import {isWalletPlatformEnabled} from '@/config/WalletAvailabilityConfig'

export type WalletAdapterContext = ActionContext<TWalletAdapterState, RootState>

type TWalletImplementation = keyof TWalletImplementations

type SessionTarget = {
  walletImplementation: TWalletImplementation
  network?: TWalletNetwork
}

export const WALLET_MUTATION = {
  ADDRESS: 'POPULATE_ADDRESS',
  CONNECTED_WALLET_TYPE: 'POPULATE_CONNECTED_WALLET_TYPE',
  SDK_STATUS: 'POPULATE_SDK_STATUS',
  URI: 'POPULATE_URI',
  LAST_WALLET: 'POPULATE_LAST_WALLET_CONNECTED',
  SYNC_ACTIVE_SESSIONS: 'SYNC_ACTIVE_SESSIONS',
}

const walletStorageId = (
  walletImplementation: TWalletImplementation,
  network: TWalletNetwork
) =>
  `${walletImplementation === 'n3' ? 'n3' : 'x'}LastWalletConnected:${network}`

const ON_DEMAND_WALLET_PLATFORMS = [
  WalletPlatform.NEOLINE,
  WalletPlatform.METAMASK,
]

const RESTORED_THROUGH_WALLET_PLATFORMS = [
  WalletPlatform.NEOLINE,
  WalletPlatform.ONEGATE,
  WalletPlatform.METAMASK,
]

const legacyWalletStorageId: Record<TWalletImplementation, string> = {
  n3: 'n3LastWalletConnected',
  neoX: 'xLastWalletConnected',
}

const createSessionState = (network: TWalletNetwork): TWalletSessionState => ({
  address: null,
  lastWalletConnected: null,
  connectedWalletPlatform: null,
  walletsStatus: {
    [WalletPlatform.NEON]: null,
    [WalletPlatform.NEOLINE]: null,
    [WalletPlatform.ONEGATE]: null,
    [WalletPlatform.METAMASK]: null,
  },
  uri: null,
  network,
})

const createInitialState = (): TWalletAdapterState => {
  const sessions = {
    n3: {
      mainnet: createSessionState('mainnet'),
      testnet: createSessionState('testnet'),
    },
    neoX: {
      mainnet: createSessionState('mainnet'),
      testnet: createSessionState('testnet'),
    },
  }

  return {
    sessions,
    networksInUse: {
      n3: walletNetworksInUse(),
      neoX: walletNetworksInUse(),
    },
    n3: sessions.n3[siteNetwork()],
    neoX: sessions.neoX[siteNetwork()],
  }
}

const sessionOf = (
  state: TWalletAdapterState,
  walletImplementation: TWalletImplementation,
  network: TWalletNetwork
): TWalletSessionState => state.sessions[walletImplementation][network]

const pendingWalletConnectApprovals = new Map<string, () => Promise<void>>()

const walletConnectPendingKey = (
  walletImplementation: TWalletImplementation,
  network: TWalletNetwork,
  walletPlatform: WalletPlatform
) => `${walletImplementation}:${network}:${walletPlatform}`

const clearWalletConnectPending = (
  walletImplementation: TWalletImplementation,
  network: TWalletNetwork,
  walletPlatform: WalletPlatform
) => {
  pendingWalletConnectApprovals.delete(
    walletConnectPendingKey(walletImplementation, network, walletPlatform)
  )
}

const readStoredLastWallet = (
  walletImplementation: TWalletImplementation,
  network: TWalletNetwork
): LastWalletConnected | null => {
  if (typeof localStorage === 'undefined') {
    return null
  }

  const stored =
    localStorage.getItem(walletStorageId(walletImplementation, network)) ??
    (network === 'mainnet'
      ? localStorage.getItem(legacyWalletStorageId[walletImplementation])
      : null)

  if (!stored?.trim()) {
    return null
  }

  try {
    return JSON.parse(stored.trim()) as LastWalletConnected
  } catch {
    return null
  }
}

@AccessorHandler
export class WalletAdapterModule
  implements Module<TWalletAdapterState, RootState> {
  namespaced = true

  state: TWalletAdapterState = createInitialState()

  getters = {
    n3LastWalletConnected: (state: TWalletAdapterState) =>
      state.n3.lastWalletConnected,
    n3ConnectedWalletPlatform: (state: TWalletAdapterState) =>
      state.n3.connectedWalletPlatform,
    n3Address: (state: TWalletAdapterState) => state.n3.address,
    n3Uri: (state: TWalletAdapterState) => state.n3.uri,
    n3Network: (state: TWalletAdapterState) => state.n3.network,
    xLastWalletConnected: (state: TWalletAdapterState) =>
      state.neoX.lastWalletConnected,
    xConnectedWalletPlatform: (state: TWalletAdapterState) =>
      state.neoX.connectedWalletPlatform,
    xAddress: (state: TWalletAdapterState) => state.neoX.address,
    xUri: (state: TWalletAdapterState) => state.neoX.uri,
    xNetwork: (state: TWalletAdapterState) => state.neoX.network,
    sessions: (state: TWalletAdapterState) => state.sessions,
    networksInUse: (state: TWalletAdapterState) => state.networksInUse,
  }

  actions = {
    async init(context: WalletAdapterContext, target: SessionTarget) {
      const {walletImplementation} = target
      const networks = target.network ? [target.network] : walletNetworksInUse()

      context.commit(WALLET_MUTATION.SYNC_ACTIVE_SESSIONS)

      await Promise.all(
        networks.map(network =>
          WalletAdapterModule.initSession(
            context,
            walletImplementation,
            network
          )
        )
      )
    },

    async connect(
      context: WalletAdapterContext,
      {
        walletImplementation,
        walletPlatform,
        network,
      }: SessionTarget & {walletPlatform: WalletPlatform}
    ) {
      const resolvedNetwork = WalletAdapterModule.resolveNetwork(
        walletImplementation,
        network
      )

      if (
        sessionOf(context.state, walletImplementation, resolvedNetwork)
          .connectedWalletPlatform
      ) {
        return true
      }

      await WalletAdapterModule.ensureWalletPlatformStarted(
        context,
        walletImplementation,
        resolvedNetwork,
        walletPlatform
      )

      const walletAdapter = getWalletAdapter(
        walletImplementation,
        resolvedNetwork,
        walletPlatform
      )
      const {approval, uri} = await walletAdapter.connect()

      context.commit(WALLET_MUTATION.URI, {
        walletImplementation,
        network: resolvedNetwork,
        uri,
      })
      await approval()

      return WalletAdapterModule.finalizeConnection(
        context,
        walletImplementation,
        resolvedNetwork,
        walletPlatform,
        walletAdapter
      )
    },

    async beginWalletConnectPairing(
      context: WalletAdapterContext,
      {
        walletImplementation,
        walletPlatform,
        network,
      }: SessionTarget & {walletPlatform: WalletPlatform}
    ) {
      const resolvedNetwork = WalletAdapterModule.resolveNetwork(
        walletImplementation,
        network
      )

      clearWalletConnectPending(
        walletImplementation,
        resolvedNetwork,
        walletPlatform
      )

      await WalletAdapterModule.ensureWalletPlatformStarted(
        context,
        walletImplementation,
        resolvedNetwork,
        walletPlatform
      )

      const walletAdapter = getWalletAdapter(
        walletImplementation,
        resolvedNetwork,
        walletPlatform
      )
      const {approval, uri} = await walletAdapter.connect()

      context.commit(WALLET_MUTATION.URI, {
        walletImplementation,
        network: resolvedNetwork,
        uri,
      })

      pendingWalletConnectApprovals.set(
        walletConnectPendingKey(
          walletImplementation,
          resolvedNetwork,
          walletPlatform
        ),
        approval
      )
    },

    async completeWalletConnectPairing(
      context: WalletAdapterContext,
      {
        walletImplementation,
        walletPlatform,
        network,
      }: SessionTarget & {walletPlatform: WalletPlatform}
    ) {
      const resolvedNetwork = WalletAdapterModule.resolveNetwork(
        walletImplementation,
        network
      )
      const key = walletConnectPendingKey(
        walletImplementation,
        resolvedNetwork,
        walletPlatform
      )
      const approval = pendingWalletConnectApprovals.get(key)

      if (!approval) {
        throw new Error('WalletConnect pairing was not started')
      }

      await approval()
      clearWalletConnectPending(
        walletImplementation,
        resolvedNetwork,
        walletPlatform
      )

      const walletAdapter = getWalletAdapter(
        walletImplementation,
        resolvedNetwork,
        walletPlatform
      )

      return WalletAdapterModule.finalizeConnection(
        context,
        walletImplementation,
        resolvedNetwork,
        walletPlatform,
        walletAdapter
      )
    },

    cancelWalletConnectPairing(
      context: WalletAdapterContext,
      {
        walletImplementation,
        walletPlatform,
        network,
      }: SessionTarget & {walletPlatform: WalletPlatform}
    ) {
      const resolvedNetwork = WalletAdapterModule.resolveNetwork(
        walletImplementation,
        network
      )

      clearWalletConnectPending(
        walletImplementation,
        resolvedNetwork,
        walletPlatform
      )

      context.commit(WALLET_MUTATION.URI, {
        walletImplementation,
        network: resolvedNetwork,
        uri: null,
      })
    },

    async disconnect(context: WalletAdapterContext, target: SessionTarget) {
      const {walletImplementation} = target
      const network = WalletAdapterModule.resolveNetwork(
        walletImplementation,
        target.network
      )
      const walletPlatform = sessionOf(
        context.state,
        walletImplementation,
        network
      ).connectedWalletPlatform

      if (!walletPlatform) {
        return false
      }

      const walletAdapter = getWalletAdapter(
        walletImplementation,
        network,
        walletPlatform
      )

      try {
        await walletAdapter.disconnect?.()
      } catch (e) {
        console.warn(
          '[Force Disconnect] Error during adapter disconnect, continuing anyway:',
          e
        )
      }

      WalletAdapterModule.clearSession(context, walletImplementation, network)
    },

    async canUseWallet(
      context: WalletAdapterContext,
      {
        walletImplementation,
        walletPlatform,
        network,
      }: SessionTarget & {walletPlatform: WalletPlatform}
    ) {
      const resolvedNetwork = WalletAdapterModule.resolveNetwork(
        walletImplementation,
        network
      )
      const walletAdapter = getWalletAdapter(
        walletImplementation,
        resolvedNetwork,
        walletPlatform
      )

      if (!walletAdapter.isInstalled()) {
        return false
      }

      const statusOf = () =>
        sessionOf(context.state, walletImplementation, resolvedNetwork)
          .walletsStatus[walletPlatform]

      if (statusOf() !== 'started') {
        await WalletAdapterModule.initWalletPlatform(
          context,
          walletImplementation,
          resolvedNetwork,
          walletPlatform
        )
      }

      return statusOf() === 'started'
    },

    async invoke(
      context: WalletAdapterContext,
      {params, walletImplementation, network}: SessionTarget & {params: any}
    ) {
      const resolvedNetwork = WalletAdapterModule.resolveNetwork(
        walletImplementation,
        network
      )
      const session = sessionOf(
        context.state,
        walletImplementation,
        resolvedNetwork
      )
      const walletPlatform = session.connectedWalletPlatform

      if (!walletPlatform) {
        throw WalletNotConnected
      }

      const walletAdapter = getWalletAdapter(
        walletImplementation,
        resolvedNetwork,
        walletPlatform
      )

      if (session.walletsStatus[walletPlatform] !== 'started') {
        throw UnimplementedWallet
      }

      return await walletAdapter.invoke(params)
    },

    async executeWithConnectedWallet(
      context: WalletAdapterContext,
      {
        actionToDoAfterConnect,
        walletImplementation,
        network,
      }: SessionTarget & {actionToDoAfterConnect: () => Promise<void> | void}
    ) {
      const resolvedNetwork = WalletAdapterModule.resolveNetwork(
        walletImplementation,
        network
      )

      if (
        sessionOf(context.state, walletImplementation, resolvedNetwork)
          .connectedWalletPlatform != null
      ) {
        await actionToDoAfterConnect()
        return
      }

      $?.modal.open('connectWalletModal', {
        actionToDoAfterConnect,
      })
    },
    syncActiveSessions(context: WalletAdapterContext) {
      context.commit(WALLET_MUTATION.SYNC_ACTIVE_SESSIONS)
    },
  }

  mutations = {
    SYNC_ACTIVE_SESSIONS(state: TWalletAdapterState) {
      state.n3 = state.sessions.n3[siteNetwork()]
      state.neoX = state.sessions.neoX[siteNetwork()]
      state.networksInUse = {
        n3: walletNetworksInUse(),
        neoX: walletNetworksInUse(),
      }
    },

    POPULATE_ADDRESS(
      state: TWalletAdapterState,
      {
        walletImplementation,
        network,
        address,
      }: {
        walletImplementation: TWalletImplementation
        network: TWalletNetwork
        address: string | null
      }
    ) {
      sessionOf(state, walletImplementation, network).address = address
    },

    POPULATE_CONNECTED_WALLET_TYPE(
      state: TWalletAdapterState,
      {
        walletImplementation,
        network,
        connectedWalletPlatform,
      }: {
        walletImplementation: TWalletImplementation
        network: TWalletNetwork
        connectedWalletPlatform: WalletPlatform | null
      }
    ) {
      sessionOf(
        state,
        walletImplementation,
        network
      ).connectedWalletPlatform = connectedWalletPlatform
    },

    POPULATE_SDK_STATUS(
      state: TWalletAdapterState,
      {
        walletImplementation,
        network,
        ...params
      }: PopulateSdkStatusParams & {
        walletImplementation: TWalletImplementation
        network: TWalletNetwork
      }
    ) {
      const session = sessionOf(state, walletImplementation, network)

      session.walletsStatus = cloneDeep({
        ...session.walletsStatus,
        [params.wallet]: params.status,
      })
    },

    POPULATE_URI(
      state: TWalletAdapterState,
      {
        walletImplementation,
        network,
        uri,
      }: {
        walletImplementation: TWalletImplementation
        network: TWalletNetwork
        uri: string | null
      }
    ) {
      sessionOf(state, walletImplementation, network).uri = uri
    },

    POPULATE_LAST_WALLET_CONNECTED(
      state: TWalletAdapterState,
      {
        walletImplementation,
        network,
        lastWalletConnected,
      }: {
        walletImplementation: TWalletImplementation
        network: TWalletNetwork
        lastWalletConnected: LastWalletConnected | null
      }
    ) {
      sessionOf(
        state,
        walletImplementation,
        network
      ).lastWalletConnected = lastWalletConnected

      if (typeof localStorage === 'undefined') {
        return
      }

      const storageId = walletStorageId(walletImplementation, network)

      if (lastWalletConnected) {
        localStorage.setItem(storageId, JSON.stringify(lastWalletConnected))
      } else {
        localStorage.removeItem(storageId)
      }

      if (network === 'mainnet') {
        localStorage.removeItem(legacyWalletStorageId[walletImplementation])
      }
    },
  }

  private static resolveNetwork(
    walletImplementation: TWalletImplementation,
    network?: TWalletNetwork
  ): TWalletNetwork {
    return network ?? siteNetwork()
  }

  private static sessionDeleteHandler(
    context: WalletAdapterContext,
    walletImplementation: TWalletImplementation,
    network: TWalletNetwork
  ) {
    return () => {
      const connectedPlatform = sessionOf(
        context.state,
        walletImplementation,
        network
      ).connectedWalletPlatform

      let keepStoredWallet = false

      if (connectedPlatform) {
        const walletAdapter = getWalletAdapter(
          walletImplementation,
          network,
          connectedPlatform
        )

        keepStoredWallet = walletAdapter.restoreBlockedByNetwork()

        walletAdapter.disconnect?.()
      }

      WalletAdapterModule.clearSession(
        context,
        walletImplementation,
        network,
        keepStoredWallet
      )
    }
  }

  private static async initWalletPlatform(
    context: WalletAdapterContext,
    walletImplementation: TWalletImplementation,
    network: TWalletNetwork,
    walletPlatform: WalletPlatform
  ) {
    const walletAdapter = getWalletAdapter(
      walletImplementation,
      network,
      walletPlatform
    )

    try {
      await walletAdapter.init(
        WalletAdapterModule.sessionDeleteHandler(
          context,
          walletImplementation,
          network
        )
      )

      context.commit(WALLET_MUTATION.SDK_STATUS, {
        walletImplementation,
        network,
        wallet: walletAdapter.platform,
        status: 'started',
      })
    } catch (error) {
      console.error(error)

      context.commit(WALLET_MUTATION.SDK_STATUS, {
        walletImplementation,
        network,
        wallet: walletAdapter.platform,
        status: 'error',
      })
    }
  }

  /**
   * NeoLine answers the first dAPI call with its own dialogs — asking to switch
   * the network, or that no account is imported — so starting it on page load
   * turns opening the site into a wallet prompt nobody asked for. It only boots
   * here when a previous session is waiting to be restored; otherwise it starts
   * when the user actually asks to connect.
   */
  private static startsOnBoot(
    walletPlatform: WalletPlatform,
    storedWalletPlatform: WalletPlatform | null
  ): boolean {
    return (
      !ON_DEMAND_WALLET_PLATFORMS.includes(walletPlatform) ||
      storedWalletPlatform === walletPlatform
    )
  }

  private static async ensureWalletPlatformStarted(
    context: WalletAdapterContext,
    walletImplementation: TWalletImplementation,
    network: TWalletNetwork,
    walletPlatform: WalletPlatform
  ) {
    const status = sessionOf(context.state, walletImplementation, network)
      .walletsStatus[walletPlatform]

    if (status === 'started') {
      return
    }

    await WalletAdapterModule.initWalletPlatform(
      context,
      walletImplementation,
      network,
      walletPlatform
    )
  }

  private static async initSession(
    context: WalletAdapterContext,
    walletImplementation: TWalletImplementation,
    network: TWalletNetwork
  ) {
    const storedWalletPlatform =
      readStoredLastWallet(walletImplementation, network)?.walletPlatform ??
      null

    await Promise.all(
      Object.values(WalletPlatform)
        .filter(walletPlatform =>
          isWalletPlatformEnabled(walletPlatform, walletImplementation)
        )
        .filter(walletPlatform =>
          WalletAdapterModule.startsOnBoot(walletPlatform, storedWalletPlatform)
        )
        .map(walletPlatform =>
          WalletAdapterModule.initWalletPlatform(
            context,
            walletImplementation,
            network,
            walletPlatform
          )
        )
    )

    await WalletAdapterModule.restoreStoredSession(
      context,
      walletImplementation,
      network
    )
  }

  private static async restoreStoredSession(
    context: WalletAdapterContext,
    walletImplementation: TWalletImplementation,
    network: TWalletNetwork
  ) {
    const lastWalletConnected = readStoredLastWallet(
      walletImplementation,
      network
    )

    if (!lastWalletConnected?.walletPlatform) {
      return
    }

    // A session stored before the wallet was turned off for this chain: drop it
    // for good, otherwise every page load restores a wallet the site no longer
    // offers and there is no button to disconnect it from.
    if (
      !isWalletPlatformEnabled(
        lastWalletConnected.walletPlatform,
        walletImplementation
      )
    ) {
      WalletAdapterModule.clearSession(context, walletImplementation, network)
      return
    }

    const adapter = getWalletAdapter(
      walletImplementation,
      network,
      lastWalletConnected.walletPlatform
    )

    let address = adapter.getAddress()

    if (
      !address &&
      RESTORED_THROUGH_WALLET_PLATFORMS.includes(
        lastWalletConnected.walletPlatform
      )
    ) {
      await adapter.restoreSession?.()
      address = adapter.getAddress()
    }

    if (!address) {
      WalletAdapterModule.clearSession(
        context,
        walletImplementation,
        network,
        adapter.restoreBlockedByNetwork()
      )

      return
    }

    context.commit(WALLET_MUTATION.ADDRESS, {
      walletImplementation,
      network,
      address,
    })
    context.commit(WALLET_MUTATION.CONNECTED_WALLET_TYPE, {
      walletImplementation,
      network,
      connectedWalletPlatform: lastWalletConnected.walletPlatform,
    })
    context.commit(WALLET_MUTATION.LAST_WALLET, {
      walletImplementation,
      network,
      lastWalletConnected: {
        address,
        walletPlatform: lastWalletConnected.walletPlatform,
      },
    })
  }

  /**
   * `keepStoredWallet` drops the live session but leaves the stored one alone: a
   * wallet sitting on another network has not disowned the session, and forgetting
   * it here is what turns a wallet the user moves away and back into a wallet the
   * site makes them connect again.
   */
  private static clearSession(
    context: WalletAdapterContext,
    walletImplementation: TWalletImplementation,
    network: TWalletNetwork,
    keepStoredWallet = false
  ) {
    context.commit(WALLET_MUTATION.ADDRESS, {
      walletImplementation,
      network,
      address: null,
    })
    context.commit(WALLET_MUTATION.CONNECTED_WALLET_TYPE, {
      walletImplementation,
      network,
      connectedWalletPlatform: null,
    })

    if (!keepStoredWallet) {
      context.commit(WALLET_MUTATION.LAST_WALLET, {
        walletImplementation,
        network,
        lastWalletConnected: null,
      })
    }

    context.commit(WALLET_MUTATION.URI, {
      walletImplementation,
      network,
      uri: null,
    })
  }

  private static async finalizeConnection(
    context: WalletAdapterContext,
    walletImplementation: TWalletImplementation,
    network: TWalletNetwork,
    walletPlatform: WalletPlatform,
    walletAdapter: WalletAdapter
  ) {
    context.commit(WALLET_MUTATION.CONNECTED_WALLET_TYPE, {
      walletImplementation,
      network,
      connectedWalletPlatform: walletPlatform,
    })

    const address = walletAdapter.getAddress()

    if (address) {
      context.commit(WALLET_MUTATION.ADDRESS, {
        walletImplementation,
        network,
        address,
      })
      context.commit(WALLET_MUTATION.LAST_WALLET, {
        walletImplementation,
        network,
        lastWalletConnected: {address, walletPlatform},
      })
      context.commit(WALLET_MUTATION.URI, {
        walletImplementation,
        network,
        uri: null,
      })

      $.modal?.close('connectWalletModal')
      $.toast.success('system.success.walletConnected')

      return true
    }

    throw RejectedByUser
  }
}
